import clsx from "clsx";
import type { HTMLAttributes, ReactNode } from "react";
import styles from "./layout.module.css";

// Карточки, плашки-показатели, значки, заглушки. Ничего умного — но именно
// из этого собраны все экраны админки и половина игры.

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  flat?: boolean;
}

export function Card({ title, subtitle, actions, flat, children, className, ...rest }: CardProps) {
  return (
    <section className={clsx(styles.card, flat && styles.flat, className)} {...rest}>
      {title || actions ? (
        <header className={styles.cardHeader}>
          <div>
            {title ? <h3 className={styles.cardTitle}>{title}</h3> : null}
            {subtitle ? <p className={styles.cardSubtitle}>{subtitle}</p> : null}
          </div>
          {actions}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  trend?: "up" | "down" | null;
  className?: string;
}

export function StatTile({ label, value, hint, trend, className }: StatTileProps) {
  return (
    <div className={clsx(styles.stat, className)}>
      <span className={styles.statLabel}>{label}</span>
      <span
        className={clsx(
          styles.statValue,
          trend === "up" && styles.trendUp,
          trend === "down" && styles.trendDown,
        )}
      >
        {value}
      </span>
      {hint ? <span className={styles.statHint}>{hint}</span> : null}
    </div>
  );
}

export function StatGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.grid, className)}>{children}</div>;
}

export type BadgeTone = "accent" | "neutral" | "success" | "warning" | "danger";

export function Badge({
  tone = "accent",
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  const toneClass = {
    accent: undefined,
    neutral: styles.badgeNeutral,
    success: styles.badgeSuccess,
    warning: styles.badgeWarning,
    danger: styles.badgeDanger,
  }[tone];
  return <span className={clsx(styles.badge, toneClass, className)}>{children}</span>;
}

export function Avatar({
  src,
  name,
  size = 36,
  className,
}: {
  src?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}) {
  // берём первые буквы слов, пропуская скобки и прочие знаки
  const initials = (name ?? "?")
    .split(/\s+/)
    .map((part) => part.match(/\p{L}/u)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .map((letter) => letter!.toUpperCase())
    .join("");

  return (
    <span className={clsx(styles.avatar, className)} style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" width={size} height={size} /> : initials}
    </span>
  );
}

export function Progress({ value, max = 100, className }: { value: number; max?: number; className?: string }) {
  const percent = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div
      className={clsx(styles.progress, className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max}
      aria-valuemin={0}
    >
      <div className={styles.progressBar} style={{ width: `${percent}%` }} />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      {icon}
      <span className={styles.emptyTitle}>{title}</span>
      {description ? <span>{description}</span> : null}
      {action}
    </div>
  );
}

export function Skeleton({
  width = "100%",
  height = 16,
  className,
}: {
  width?: number | string;
  height?: number | string;
  className?: string;
}) {
  return <div className={clsx(styles.skeleton, className)} style={{ width, height }} />;
}

export function Breadcrumbs({ items }: { items: { label: ReactNode; onClick?: () => void }[] }) {
  return (
    <nav className={styles.breadcrumbs} aria-label="Хлебные крошки">
      {items.map((item, index) => (
        <span key={index}>
          {index > 0 ? <span className={styles.breadcrumbSep}>/</span> : null}{" "}
          {item.onClick ? (
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                item.onClick?.();
              }}
            >
              {item.label}
            </a>
          ) : (
            item.label
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className={styles.pageHeader}>
      <div>
        <h1 className={styles.pageTitle}>{title}</h1>
        {description ? <p className={styles.pageDescription}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.toolbar}>{actions}</div> : null}
    </header>
  );
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.toolbar, className)}>{children}</div>;
}
